// @ts-nocheck
import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Function to scroll to contact section in the footer
const scrollToContact = () => {
  const footerSection = document.getElementById('footer-section');
  if (footerSection) {
    footerSection.scrollIntoView({ behavior: 'smooth' });
  }
};

const PricingCard = ({ price, title, description, features, buttonText = "Get started" }) => (
  <motion.div 
    variants={fadeInUp}
    className="bg-gradient-to-b from-black to-[#001139] rounded-lg p-8 flex flex-col h-full min-h-[500px] border border-gray-800"
  >
    <div className="mb-6">
      <h3 className="text-2xl font-bold text-white">${price}</h3>
      <h4 className="text-lg font-semibold text-white mt-2">{title}</h4>
      <p className="text-gray-400 text-sm mt-2">{description}</p>
    </div>
    
    <div className="flex-grow">
      <ul className="space-y-4">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-300 text-sm">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
    
    <button 
      className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded text-center"
      onClick={scrollToContact}
    >
      {buttonText}
    </button>
  </motion.div>
);

const PricingSection = () => {
  const [formData, setFormData] = useState({
    service: '',
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const generateMailtoLink = () => {
    const subject = encodeURIComponent(`Custom Package Request: ${formData.service}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Phone: ${formData.phone}\n` +
      `Email: ${formData.email}\n` +
      `Service: ${formData.service}\n` +
      `Message: ${formData.message}\n\n` +
      `This message was sent from the Link Bridge Consulting website contact form.`
    );
    
    return `mailto:connect@Linkbridgellc.us?subject=${subject}&body=${body}`;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if all required fields are filled
    if (!formData.name || !formData.phone || !formData.email || !formData.service) {
      alert("Please fill all required fields");
      return;
    }
    
    // Open the user's email client with pre-filled information
    window.location.href = generateMailtoLink();
    
    // Mark form as submitted
    setFormSubmitted(true);
  };

  const pricingPlans = [
    {
      price: "600",
      title: "Tailored brand opportunity research",
      description: "Build comprehensive scoping project for brands that work",
      features: [
        "Create advisory services",
        "Market funding options",
        "Funding research, Grant, VC & more",
        "Reports with key metrics and tags",
        "Startup roadmap tailored to your path"
      ]
    },
    {
      price: "1250",
      title: "Project deck design",
      description: "Create project decks that work",
      features: [
        "Attractive visuals that get your point",
        "Clear and compelling storytelling",
        "Professionally designed investor ready deck (up to 15 slides)",
        "Web elements & enhanced content flow"
      ]
    },
    {
      price: "700",
      title: "Website design (mvp)",
      description: "Get a visually appealing and user-friendly website design",
      features: [
        "Custom brand-aligned UI/UX design",
        "Responsive layout for all devices",
        "Modern connection to your workspace",
        "Ready-to-deploy design files"
      ]
    },
    {
      price: "1500",
      title: "Website + SEO link support",
      description: "Get a working website with expert launch support",
      features: [
        "Custom brand-aligned UI/UX design",
        "Responsive layout for all devices",
        "Development ready design files",
        "Pre-launch testing and performance checks",
        "Customer management and handover"
      ]
    },
    {
      price: "1200",
      title: "Digital communication (Outreach)",
      description: "Streamline and maximize your project visibility",
      features: [
        "Verified connection access for privacy",
        "Collaborative setup for team accessibility",
        "Targeted LinkedIn or Medium for business use",
        "Clear guides, workflows, and references",
        "Structured and well-organized documentation"
      ]
    },
    {
      price: "250 - 1000",
      title: "Cover letter + proposal support",
      description: "Maximize your chances of winning funding",
      features: [
        "Grant writing services",
        "Review and feedback on grant proposals",
        "Assistance with crafting compelling grant applications",
        "Clear guides, workflows, and references",
        "Pricing depends on the level of support required"
      ]
    },
    {
      price: "5000",
      title: "Full service package",
      description: "Strategic level complete services",
      features: [
        "Premium-grade applications",
        "Full deck design (up to 15 slides)",
        "Advisory services",
        "Project documentation (Guidelines/Notes)",
        "Full project website with go-live support"
      ]
    },
    {
      price: "1200",
      title: "Project management services",
      description: "Ensure effective planning and execution",
      features: [
        "Advisory services",
        "Oversee project milestones",
        "Time management and resource allocation",
        "Regular reporting and updating",
        "Ending and project planning and execution"
      ]
    }
  ];
  
  return (
    <section id="pricing" className="min-h-screen pt-28 pb-32 bg-black">
      <div className="container mx-auto px-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Choose the package that best fit your team's needs,
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            whether you are getting started or managing large projects.
          </motion.p>
        </motion.div>
        
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
        >
          {pricingPlans.map((plan, index) => (
            <PricingCard
              key={index}
              price={plan.price}
              title={plan.title}
              description={plan.description}
              features={plan.features}
            />
          ))}
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-24 bg-black rounded-lg p-6 md:p-8 border border-gray-800"
          id="contact-form"
        >
          {formSubmitted ? (
            <div className="text-center py-8">
              <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <h3 className="text-2xl font-bold text-white mb-3">Thank you for your interest!</h3>
              <p className="text-gray-300 mb-6">Your email client has been opened with all your information. Just click send to complete your request.</p>
              <button 
                onClick={() => setFormSubmitted(false)}
                className="bg-gray-700 hover:bg-gray-800 text-white py-2 px-6 rounded transition-colors duration-300"
              >
                Submit Another Request
              </button>
                </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-3">Need a Custom Package?</h3>
                <p className="text-gray-300">
                  We understand that every project is unique. Fill out the form below to discuss a tailored solution.
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                    <label htmlFor="service" className="block text-gray-300 text-sm mb-1 md:mb-2">Service required*</label>
                      <select 
                        id="service" 
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                      className="w-full bg-white border border-gray-300 rounded py-2 md:py-3 px-3 md:px-4 text-black h-10 md:h-12 text-sm"
                        required
                      >
                        <option value="">Select a service</option>
                        <option value="Web Design">Web Design</option>
                        <option value="Brand Research">Brand Research</option>
                        <option value="Project Management">Project Management</option>
                        <option value="Custom Package">Custom Package</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="name" className="block text-gray-300 text-sm mb-1 md:mb-2">Full name*</label>
                      <input 
                        type="text" 
                        id="name" 
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name" 
                      className="w-full bg-white border border-gray-300 rounded py-2 md:py-3 px-3 md:px-4 text-black placeholder-black/70 h-10 md:h-12 text-sm"
                        required 
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-gray-300 text-sm mb-1 md:mb-2">Phone Number*</label>
                      <input 
                        type="tel" 
                        id="phone" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter your phone number" 
                      className="w-full bg-white border border-gray-300 rounded py-2 md:py-3 px-3 md:px-4 text-black placeholder-black/70 h-10 md:h-12 text-sm"
                        required 
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-gray-300 text-sm mb-1 md:mb-2">Email address*</label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email address" 
                      className="w-full bg-white border border-gray-300 rounded py-2 md:py-3 px-3 md:px-4 text-black placeholder-black/70 h-10 md:h-12 text-sm"
                        required 
                      />
                    </div>
                  </div>
                  
                <div className="mt-4">
                  <label htmlFor="message" className="block text-gray-300 text-sm mb-1 md:mb-2">Additional details (optional)</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your project requirements..."
                    className="w-full bg-white border border-gray-300 rounded py-2 md:py-3 px-3 md:px-4 text-black placeholder-black/70 h-24 text-sm"
                  ></textarea>
                </div>
                
                <div className="mt-6 flex flex-col md:flex-row md:items-center justify-between">
                  <button 
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white py-2 md:py-3 px-6 md:px-10 rounded text-sm md:text-base transition-all duration-300"
                  >
                    Submit Request
                  </button>
                  
                  <div className="mt-4 md:mt-0 text-gray-400 text-xs md:text-sm">
                    <span>Or email us directly at: </span>
                    <a 
                      href="mailto:connect@Linkbridgellc.us" 
                      className="text-blue-400 hover:underline"
                    >
                      connect@Linkbridgellc.us
                    </a>
                  </div>
                </div>
              </form>
            </>
              )}
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection; 